import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AtualizarMarcaEntrada,
  AtualizarPerfilEntrada,
  ConectarBlueskyEntrada,
  CriarMarcaEntrada,
  CriarPerfilEntrada,
  FiltroDePosts,
  MarcaDto,
  MidiaDePostDto,
  PerfilSocialDto,
  PostDto,
  SalvarPostEntrada,
  SituacaoDasConexoesDto,
} from '@crm/shared'
import { api, enviarArquivo } from './cliente'

/** FaroEscolar Marketing (ADR 0057): marcas, perfis e posts das redes. */

const MARCAS = ['marketing', 'marcas']
const POSTS = ['marketing', 'posts']

export function useMarcas() {
  return useQuery({
    queryKey: MARCAS,
    queryFn: () => api<MarcaDto[]>('/api/marketing/marcas'),
    staleTime: 60_000,
  })
}

export function useCriarMarca() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarMarcaEntrada) =>
      api<MarcaDto>('/api/marketing/marcas', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: MARCAS }),
  })
}

export function useEditarMarca() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string } & AtualizarMarcaEntrada) =>
      api<MarcaDto>(`/api/marketing/marcas/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: MARCAS })
      void cliente.invalidateQueries({ queryKey: POSTS })
    },
  })
}

export function useExcluirMarca() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, confirmar }: { id: string; confirmar?: boolean }) =>
      api<{ ok: true }>(`/api/marketing/marcas/${id}`, {
        metodo: 'DELETE',
        query: confirmar ? { confirmar: 'sim' } : undefined,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: MARCAS })
      void cliente.invalidateQueries({ queryKey: POSTS })
    },
  })
}

export function useCriarPerfil() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarPerfilEntrada) =>
      api<PerfilSocialDto>('/api/marketing/perfis', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: MARCAS }),
  })
}

export function useEditarPerfil() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string } & AtualizarPerfilEntrada) =>
      api<PerfilSocialDto>(`/api/marketing/perfis/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: MARCAS }),
  })
}

export function useExcluirPerfil() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/marketing/perfis/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: MARCAS }),
  })
}

export function usePosts(filtro: FiltroDePosts) {
  return useQuery({
    queryKey: [...POSTS, filtro],
    queryFn: () =>
      api<PostDto[]>('/api/marketing/posts', {
        query: Object.fromEntries(Object.entries(filtro).filter(([, v]) => v)) as Record<
          string,
          string
        >,
      }),
    // A hora de publicar muda o status sozinha, no worker.
    refetchInterval: 60_000,
  })
}

export function usePost(id: string | null) {
  return useQuery({
    queryKey: [...POSTS, 'um', id],
    queryFn: () => api<PostDto>(`/api/marketing/posts/${id}`),
    enabled: Boolean(id),
    /*
      O worker muda o post sozinho (hora de publicar, "Publicando…", publicado,
      falhou). Sem isto, o post aberto ficava parado em "Publicando…" até sair
      e voltar.
    */
    refetchInterval: (consulta) => {
      const post = consulta.state.data
      return post && (post.status === 'agendado' || post.status === 'na_hora') ? 30_000 : false
    },
  })
}

function aoMudarPost(cliente: ReturnType<typeof useQueryClient>) {
  return () => cliente.invalidateQueries({ queryKey: POSTS })
}

export function useSalvarPost() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string | null } & SalvarPostEntrada) =>
      id
        ? api<PostDto>(`/api/marketing/posts/${id}`, { metodo: 'PUT', corpo: dados })
        : api<PostDto>('/api/marketing/posts', { metodo: 'POST', corpo: dados }),
    onSuccess: (salvo) => {
      // O rascunho novo abre no endereço dele já com os dados, sem "Carregando…".
      cliente.setQueryData([...POSTS, 'um', salvo.id], salvo)
      return aoMudarPost(cliente)()
    },
  })
}

export function useExcluirPost() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/api/marketing/posts/${id}`, { metodo: 'DELETE' }),
    onSuccess: aoMudarPost(cliente),
  })
}

export function useAprovarPost() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<PostDto>(`/api/marketing/posts/${id}/aprovar`, { metodo: 'POST' }),
    onSuccess: aoMudarPost(cliente),
  })
}

export function useReprovarPost() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api<PostDto>(`/api/marketing/posts/${id}/reprovar`, { metodo: 'POST', corpo: { motivo } }),
    onSuccess: aoMudarPost(cliente),
  })
}

export function useMarcarPublicado() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ postId, destinoId }: { postId: string; destinoId: string }) =>
      api<PostDto>(`/api/marketing/posts/${postId}/destinos/${destinoId}/publicado`, {
        metodo: 'POST',
      }),
    onSuccess: aoMudarPost(cliente),
  })
}

export function useSubirMidiaDePost() {
  return useMutation({
    mutationFn: async (arquivo: File) => {
      const midia = await enviarArquivo<MidiaDePostDto | null>('/api/marketing/midias', arquivo)
      if (!midia) throw new Error('A resposta do envio veio vazia. Tente de novo.')
      return midia
    },
  })
}

// ─────────────────── conexões com as redes (ADR 0058) ───────────────────

const CONEXOES = ['marketing', 'conexoes']

export function useConexoesDeRede() {
  return useQuery({
    queryKey: CONEXOES,
    queryFn: () => api<SituacaoDasConexoesDto>('/api/marketing/conexoes'),
    staleTime: 30_000,
  })
}

export function useConectarBluesky() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: ConectarBlueskyEntrada) =>
      api<{ ok: true }>('/api/marketing/conexoes/bluesky', { metodo: 'POST', corpo: dados }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CONEXOES })
      void cliente.invalidateQueries({ queryKey: MARCAS })
    },
  })
}

export function useDesconectarRede() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/marketing/conexoes/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CONEXOES })
      void cliente.invalidateQueries({ queryKey: MARCAS })
    },
  })
}

export function useQuadrosDoPinterest(perfilId: string | null) {
  return useQuery({
    queryKey: ['marketing', 'quadros', perfilId],
    queryFn: () => api<{ id: string; nome: string }[]>(`/api/marketing/perfis/${perfilId}/quadros`),
    enabled: Boolean(perfilId),
    staleTime: 5 * 60_000,
  })
}

export function useTentarDeNovo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ postId, destinoId }: { postId: string; destinoId: string }) =>
      api<PostDto>(`/api/marketing/posts/${postId}/destinos/${destinoId}/tentar`, { metodo: 'POST' }),
    onSuccess: aoMudarPost(cliente),
  })
}
